import React, { useState, useEffect } from "react";
import {
  Heart,
  Phone,
  MapPin,
  Menu,
  X,
  Users,
  Mail,
  Facebook,
  Instagram,
  Clock,
  Award,
  Shield,
  MessageCircle,
  Home,
  Utensils,
  Baby,
  Car,
  Stethoscope,
  Bath,
  Shirt,
  Smile,
  BookOpen,
  Music,
  CheckCircle,
  Brain,
  HeartHandshake
} from "lucide-react";
import { Link } from "react-router-dom";
export default function ServicesPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    // Scroll to top when this page loads
    window.scrollTo(0, 0);
  }, []);
  const services = [
    {
      title: "Personal Care",
      icon: <Bath className="w-12 h-12" />,
      color: "#65c063",
      bgColor: "#dcfce7",
      description:
        "Comprehensive assistance with daily personal activities to maintain dignity and independence.",
      features: [
        "Bathing and showering assistance",
        "Dressing and grooming support(Nail and foot care included)",
        "Mobility and transfer assistance",
        "Medication reminders",
        "Toileting and incontinence care",
      ],
    },
    {
      title: "Companionship",
      icon: <Heart className="w-12 h-12" />,
      color: "#f5a09e",
      bgColor: "#ffe8e6",
      description:
        "Engaging social interaction and emotional support to enhance quality of life.",
      features: [
        "Meaningful conversation and activities",
        "Games, puzzles, and mental stimulation",
        "Reading and storytelling",
        "Walking and light exercise",
        "Accompanying to appointments",
      ],
    },
    {
      title: "Homemaking",
      icon: <Home className="w-12 h-12" />,
      color: "#65c063",
      bgColor: "#dcfce7",
      description:
        "Professional household assistance to maintain a clean, safe, and comfortable environment.",
      features: [
        "Light housekeeping and cleaning",
        "Meal planning and preparation",
        "Laundry and linen changes",
        // "Grocery shopping assistance",
        "Home organization",
      ],
    },
    {
      title: "Respite Care",
      icon: <Clock className="w-12 h-12" />,
      color: "#f5a09e",
      bgColor: "#ffe8e6",
      description:
        "Temporary relief for family caregivers while ensuring continuity of quality care.",
      features: [
        "Flexible hourly or daily care",
        "Overnight care available",
        "Planned or emergency respite",
        "Consistent care routines",
        "Regular updates to family",
      ],
    },
    {
      title: "Meal Preparation",
      icon: <Utensils className="w-12 h-12" />,
      color: "#65c063",
      bgColor: "#dcfce7",
      description:
        "Nutritious meal planning and preparation tailored to dietary needs and preferences.",
      features: [
        "Custom meal planning",
        "Dietary restriction management",
        "Fresh, healthy cooking",
        "Meal portioning and storage",
        "Kitchen cleanup",
      ],
    },
    {
      title: "Dementia Care",
      icon: <Brain className="w-12 h-12" />,
      color: "#f5a09e",
      bgColor: "#ffe8e6",
      description:
        "Specialized compassionate care for individuals living with dementia and Alzheimer's disease.",
      features: [
        "Memory care and cognitive stimulation",
        "Routine and structure maintenance",
        "Behavioral support and redirection",
        "Safe environment management",
        "Family education and support",
      ],
    },
    {
      title: "Palliative Care",
      icon: <HeartHandshake className="w-12 h-12" />,
      color: "#65c063",
      bgColor: "#dcfce7",
      description:
        "Compassionate end-of-life care focused on comfort, dignity, and quality of life.",
      features: [
        "Pain and symptom management support",
        "Emotional and spiritual support",
        "Comfort care measures",
        "Family guidance and respite",
        "Coordination with healthcare team",
      ],
    },
    // {
    //   title: "Transportation",
    //   icon: <Car className="w-12 h-12" />,
    //   color: "#f5a09e",
    //   bgColor: "#ffe8e6",
    //   description: "Safe and reliable transportation services for appointments and errands.",
    //   features: [
    //     "Medical appointment transport",
    //     "Shopping and errands",
    //     "Social event accompaniment",
    //     "Safe vehicle assistance",
    //     "Door-to-door service"
    //   ]
    // }
  ];

  const process = [
    {
      step: "01",
      title: "Initial Consultation",
      description:
        "We begin with a free consultation to understand your needs and preferences.",
      color: "#65c063",
    },
    {
      step: "02",
      title: "Care Plan Development",
      description:
        "Our team creates a personalized care plan tailored to your unique situation.",
      color: "#f5a09e",
    },
    {
      step: "03",
      title: "Caregiver Matching",
      description:
        "We carefully match you with the perfect caregiver based on personality and needs.",
      color: "#65c063",
    },
    {
      step: "04",
      title: "Ongoing Support",
      description:
        "We provide continuous monitoring and adjustments to ensure the best care.",
      color: "#f5a09e",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section
        className="relative py-20 overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #f0fdf4 0%, #fff5f4 50%, #ffe8e6 100%)",
        }}
      >
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute top-20 left-10 w-96 h-96 rounded-full blur-3xl"
            style={{ backgroundColor: "#65c063" }}
          ></div>
          <div
            className="absolute bottom-20 right-10 w-96 h-96 rounded-full blur-3xl"
            style={{ backgroundColor: "#f5a09e" }}
          ></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
          <div className="inline-block mb-4">
            <span
              className="px-4 py-2 rounded-full text-sm font-medium text-white"
              style={{
                background: "linear-gradient(135deg, #65c063, #29a060)",
              }}
            >
              Our Services
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
            Comprehensive Care
            <br />
            <span style={{ color: "#65c063" }}>Tailored </span>
            to Your
            <span style={{ color: "#f5a09e" }}> Needs</span>
          </h1>

          <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
            At Heartland Care Services, we offer a wide range of professional
            home care services designed to support independence, comfort, and
            quality of life for your loved ones.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 border-2 border-gray-100"
              >
                <div
                  className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6"
                  style={{
                    backgroundColor: service.bgColor,
                    color: service.color,
                  }}
                >
                  {service.icon}
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {service.description}
                </p>

                <div className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle
                        className="w-5 h-5 flex-shrink-0 mt-0.5"
                        style={{ color: service.color }}
                      />
                      <span className="text-sm text-gray-600">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section
        className="py-20"
        style={{
          background: "linear-gradient(135deg, #fff5f4 0%, #f0fdf4 100%)",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span
              className="font-semibold text-sm uppercase tracking-wider"
              style={{ color: "#65c063" }}
            >
              How It Works
            </span>
            <h2 className="text-4xl font-bold text-gray-900 mt-2 mb-4">
              Our Simple Process
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Getting started with Heartland Care Services is easy. We guide you
              through every step to ensure the best care for your loved ones.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div key={index} className="text-center">
                <div
                  className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center text-white text-2xl font-bold"
                  style={{ backgroundColor: item.color }}
                >
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-20 text-white relative overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #65c063 0%, #29a060 50%, #65c063 100%)",
        }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-6">Ready to Learn More?</h2>
          <p className="text-xl text-green-50 mb-8">
            Contact us today for a free consultation and let us create a
            personalized care plan for your loved ones.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              className="bg-white px-8 py-4 rounded-full font-medium hover:shadow-2xl transition-all flex items-center justify-center gap-2"
              style={{ color: "#65c063" }}
              href="tel:+16045127113"
            >
              <Phone className="w-5 h-5" />
              Call: +1 604-512-7113
            </button>
            <Link to="/book">
              <button className="border-2 border-white text-white px-8 py-4 rounded-full font-medium hover:bg-white transition-all flex items-center justify-center gap-2 hover:text-[#65c063]">
                <Mail className="w-5 h-5" />
                Schedule Consultation
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/16045127113"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-transform group"
        style={{ background: "#25D366" }}
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="w-7 h-7 text-white" />
        <span className="absolute right-16 bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
          Chat with us
        </span>
      </a>

      {/* Footer */}
    </div>
  );
}
